// ═══════════════════════════════════════════════
//   BAKERY — idle care feature
// ═══════════════════════════════════════════════
//
// Objective:   Run the konbini's idle bakery — bake goodies automatically
//              over time, collect them before they burn, and spend coins on
//              upgrades and new recipes. Stock inventory and sell to customers
//              for bakery coins, then exchange coins for store pts.
// Type:        idle-care
// Tunables:    GAME_BAKERY in data.js

const {S, addPts, toast, scheduleSave, SFX, openWin, closeWin} = window;

let _bakeryTab = 'bake';
let _bakeryTickInterval = null;

// Auto-restart pending: slotIdx → timestamp when restart fires
let _restartPending = {};

// Sell-tab state (not persisted — reconstructed each session)
let _sellOrders    = [null, null, null]; // up to 3 slots
let _slotCooldowns = [0, 0, 0];

const _CUST_FACES  = ['👤', '🧑', '👩', '👦', '🧒', '👧', '🧓'];
const _ANGRY_FACES = ['😤', '😠', '🤬', '😡', '💢'];

// ── Open / Close ───────────────────────────────

function openBakery() {
    openWin('w-bakery');
    setBakeryTab(_bakeryTab);
    if (!_bakeryTickInterval) {
        _bakeryTickInterval = setInterval(_bakeryTick, 1000);
    }
}

function closeBakery() {
    clearInterval(_bakeryTickInterval);
    _bakeryTickInterval = null;
    closeWin('w-bakery');
}

function _bakeryTick() {
    const win = document.getElementById('w-bakery');
    if (!win || win.style.display === 'none') return;

    // Lv2: auto-collect to inventory (restart handled separately via _restartPending)
    if ((S.bakeryUpgrades.autostart || 0) >= 2) {
        const count = _activeSlotCount();
        for (let i = 0; i < count; i++) {
            if (_getOvenState(S.bakeryOvens[i]).phase === 'ready') _autoCollect(i);
        }
    }

    // Fire pending auto-restarts whose delay has elapsed
    Object.keys(_restartPending).forEach(idx => {
        if (Date.now() >= _restartPending[idx]) {
            const slotIdx = parseInt(idx);
            const slot = S.bakeryOvens[slotIdx];
            if (slot && !slot.started) {
                slot.goodie = slot.sel || slot.goodie;
                slot.started = Date.now();
                scheduleSave();
            }
            delete _restartPending[idx];
        }
    });

    if (_bakeryTab === 'bake') _renderOvens();
    if (_bakeryTab === 'sell') _renderSell();
    _updateBakeryHUD();
}

// ── Tabs ───────────────────────────────────────

function setBakeryTab(tab) {
    _bakeryTab = tab;
    ['bake', 'upgrades', 'goodies', 'sell'].forEach(t => {
        const panel = document.getElementById('bkr-panel-' + t);
        const btn = document.querySelector(`.bkr-tab[data-tab="${t}"]`);
        if (panel) panel.style.display = t === tab ? '' : 'none';
        if (btn) btn.classList.toggle('on', t === tab);
    });
    _updateBkrHeader();
    if (tab === 'bake')          _renderOvens();
    else if (tab === 'upgrades') _renderUpgrades();
    else if (tab === 'goodies')  _renderGoodies();
    else if (tab === 'sell')     _renderSell();
}

function _updateBkrHeader() {
    const coinsEl = document.getElementById('bkr-coins');
    if (coinsEl) coinsEl.textContent = (S.bakeryCoins || 0).toLocaleString();
    const exchangeBtn = document.getElementById('bkr-exchange-btn');
    if (exchangeBtn && window.GAME_BAKERY) {
        const CFG = window.GAME_BAKERY;
        const blocks = Math.floor((S.bakeryCoins || 0) / CFG.exchangeMinCoins);
        const ptsAvail = blocks * (CFG.exchangeMinCoins / CFG.exchangeCoinsPerPt);
        exchangeBtn.textContent = blocks > 0 ? `→ +${ptsAvail}pts` : '→ —';
        exchangeBtn.disabled = blocks === 0;
        exchangeBtn.classList.toggle('dim', blocks === 0);
    }
    const hatEl = document.getElementById('bkr-chef-hat');
    if (hatEl) hatEl.textContent = S.bakeryUpgrades.chef ? '🧑‍🍳 ' : '';
    const body = document.getElementById('bkr-body');
    if (body) {
        const themes = [
            'linear-gradient(160deg, #fff8ec, #fff0f5)',
            'linear-gradient(160deg, #fce4ec, #fce8f2)',
            'linear-gradient(160deg, #e8f5e9, #f3e5f5)',
            'linear-gradient(160deg, #e3f2fd, #f3f0ff)',
        ];
        const lvl = Math.min(S.bakeryUpgrades.wallpaper || 0, 3);
        body.style.background = `url('assets/games/bakery_bg_lv${lvl}.png') center/cover no-repeat, ${themes[lvl]}`;
    }
}

// ── State helpers ──────────────────────────────

function _getBakeTime(goodie) {
    const CFG = window.GAME_BAKERY;
    return Math.max(CFG.bakeTimeMin, CFG.bakeTimeBase + goodie.bakeBonus - (S.bakeryUpgrades.speed || 0) * CFG.bakeTimeStep);
}

function _getBurnGrace() {
    const CFG = window.GAME_BAKERY;
    return CFG.burnGrace + (S.bakeryUpgrades.grace || 0) * CFG.burnGraceStep;
}

function _getReward(goodie) {
    const CFG = window.GAME_BAKERY;
    return CFG.yieldBase + (S.bakeryUpgrades.yield || 0) * CFG.yieldStep + goodie.rewardBonus;
}

function _getInvMax() {
    const CFG = window.GAME_BAKERY;
    return CFG.invMaxPerType + (S.bakeryUpgrades.shelf || 0) * CFG.invMaxStep;
}

function _getMaxCustomers() {
    return Math.min(3, 1 + (S.bakeryUpgrades.counter || 0));
}

function _getSellTimeout() {
    const CFG = window.GAME_BAKERY;
    return CFG.sellTimeoutBase + (S.bakeryUpgrades.charm || 0) * CFG.charmStep * 1000;
}

function _getSellMultiplier() {
    const CFG = window.GAME_BAKERY;
    return CFG.sellMultiplier + (S.bakeryUpgrades.deals || 0) * CFG.dealsStep;
}

function _getOvenState(slot) {
    if (!slot || !slot.started) return { phase: 'idle' };
    const CFG = window.GAME_BAKERY;
    const goodie = CFG.goodies.find(g => g.id === slot.goodie) || CFG.goodies[0];
    const bakeMs = _getBakeTime(goodie) * 1000;
    const graceMs = _getBurnGrace() * 1000;
    const elapsed = Date.now() - slot.started;
    if (elapsed >= bakeMs + graceMs) return { phase: 'burned', goodie };
    if (elapsed >= bakeMs) {
        return { phase: 'ready', goodie, readySec: Math.ceil((bakeMs + graceMs - elapsed) / 1000) };
    }
    return {
        phase: 'baking',
        goodie,
        pct: Math.min(100, (elapsed / bakeMs) * 100),
        secLeft: Math.ceil((bakeMs - elapsed) / 1000),
    };
}

function _activeSlotCount() {
    return Math.min(5, 1 + (S.bakeryUpgrades.oven || 0));
}

function _decorClass() {
    const level = S.bakeryUpgrades.decor || 0;
    return level > 0 ? 'bkr-decor-' + Math.min(level, 3) : '';
}

function _decorBadge() {
    const level = S.bakeryUpgrades.decor || 0;
    if (level === 0) return '';
    return `<div class="bkr-decor-badge bkr-decor-badge-lv${level}"><img src="assets/games/bakery_oven_lv${level}.png" alt="✦" class="bkr-decor-img"></div>`;
}

// ── Render ovens ───────────────────────────────

function _renderOvens() {
    const cont = document.getElementById('bkr-ovens');
    if (!cont) return;
    const CFG = window.GAME_BAKERY;
    const count = _activeSlotCount();
    let html = '<div class="bkr-ovens-grid">';

    for (let i = 0; i < 5; i++) {
        const slot = S.bakeryOvens[i];
        if (i >= count) {
            html += `<div class="bkr-oven locked">
                <div class="bkr-oven-icon">🔒</div>
                <div class="bkr-oven-body">
                    <div class="bkr-oven-name">LOCKED</div>
                    <div class="bkr-oven-hint">Buy Extra Oven to unlock</div>
                </div>
            </div>`;
            continue;
        }

        const ov = _getOvenState(slot);
        const dc = _decorClass();
        const badge = _decorBadge();
        const isPending = _restartPending[i] !== undefined;

        if (ov.phase === 'idle' && isPending) {
            const countdown = Math.max(0, Math.ceil((_restartPending[i] - Date.now()) / 1000));
            const sel = (slot && slot.sel) || 'cookie';
            const selGoodie = CFG.goodies.find(g => g.id === sel) || CFG.goodies[0];
            html += `<div class="bkr-oven idle bkr-restarting ${dc}">
                ${badge}
                <div class="bkr-oven-icon"><img src="${selGoodie.img}" alt="${selGoodie.icon}" class="bkr-recipe-img"></div>
                <div class="bkr-oven-body">
                    <div class="bkr-oven-name">RESTARTING… ${countdown}s</div>
                    <div class="bkr-oven-hint">${selGoodie.name}</div>
                </div>
                <button class="bkr-cancel-btn" onclick="event.stopPropagation();cancelAutoRestart(${i})">✕ CANCEL</button>
            </div>`;
        } else if (ov.phase === 'idle') {
            const sel = (slot && slot.sel) || 'cookie';
            const selGoodie = CFG.goodies.find(g => g.id === sel) || CFG.goodies[0];
            const selBtns = CFG.goodies
                .filter(g => S.bakeryUnlocked.includes(g.id))
                .map(g => `<button class="bkr-gsel${g.id === sel ? ' on' : ''}" onclick="event.stopPropagation();bakerySelectGoodie(${i},'${g.id}')"><img src="${g.img}" alt="${g.icon}" class="bkr-recipe-img"></button>`)
                .join('');
            html += `<div class="bkr-oven idle ${dc}">
                ${badge}
                <div class="bkr-oven-icon"><img src="${selGoodie.img}" alt="${selGoodie.icon}" class="bkr-recipe-img"></div>
                <div class="bkr-oven-body">
                    <div class="bkr-oven-name">EMPTY OVEN</div>
                    <div class="bkr-gsel-row">${selBtns}</div>
                    <div class="bkr-oven-hint">~${_getBakeTime(selGoodie)}s · +${_getReward(selGoodie)} coins</div>
                </div>
                <button class="bkr-start-btn" onclick="clickOven(${i})">START ▶</button>
            </div>`;
        } else if (ov.phase === 'baking') {
            html += `<div class="bkr-oven baking ${dc}">
                ${badge}
                <div class="bkr-oven-icon bkr-wobble"><img src="${ov.goodie.img}" alt="${ov.goodie.icon}" class="bkr-recipe-img"></div>
                <div class="bkr-oven-body">
                    <div class="bkr-oven-name">BAKING…</div>
                    <div class="bkr-prog-wrap"><div class="bkr-prog-fill" style="width:${ov.pct.toFixed(1)}%"></div></div>
                    <div class="bkr-oven-hint">${ov.secLeft}s left</div>
                </div>
                <button class="bkr-cancel-btn" onclick="event.stopPropagation();cancelBake(${i})">✕</button>
            </div>`;
        } else if (ov.phase === 'ready') {
            html += `<div class="bkr-oven ready ${dc}" onclick="clickOven(${i})">
                ${badge}
                <div class="bkr-oven-icon bkr-bounce"><img src="${ov.goodie.img}" alt="${ov.goodie.icon}" class="bkr-recipe-img"></div>
                <div class="bkr-oven-body">
                    <div class="bkr-oven-name">READY ✦</div>
                    <div class="bkr-oven-hint">burns in ${ov.readySec}s</div>
                </div>
                <button class="bkr-collect-btn" onclick="event.stopPropagation();clickOven(${i})">+${_getReward(ov.goodie)}<br><span>coins</span></button>
            </div>`;
        } else if (ov.phase === 'burned') {
            html += `<div class="bkr-oven burned ${dc}" onclick="clickOven(${i})">
                <div class="bkr-oven-icon">🔥</div>
                <div class="bkr-oven-body">
                    <div class="bkr-oven-name">BURNED!</div>
                    <div class="bkr-oven-hint">Click to clear the oven</div>
                </div>
            </div>`;
        }
    }

    html += '</div>';
    cont.innerHTML = html;
    _updateBkrHeader();
}

// ── Oven interactions ──────────────────────────

function clickOven(slotIdx) {
    if (!S.bakeryOvens[slotIdx] || typeof S.bakeryOvens[slotIdx] !== 'object') {
        S.bakeryOvens[slotIdx] = { started: null, goodie: 'cookie', sel: 'cookie' };
    }
    const slot = S.bakeryOvens[slotIdx];
    const ov = _getOvenState(slot);
    const CFG = window.GAME_BAKERY;

    if (ov.phase === 'idle') {
        if (_restartPending[slotIdx] !== undefined) return; // waiting for restart; cancel first
        const goodie = CFG.goodies.find(g => g.id === (slot.sel || 'cookie')) || CFG.goodies[0];
        slot.started = Date.now();
        slot.goodie = goodie.id;
        SFX.play('click');
        toast(`${goodie.icon} Baking ${goodie.name}! Ready in ~${_getBakeTime(goodie)}s ♡`);

    } else if (ov.phase === 'ready') {
        let coins = _getReward(ov.goodie);
        let luckyMsg = '';
        const luckyLvl = S.bakeryUpgrades.lucky || 0;
        if (luckyLvl > 0) {
            const chance = CFG.luckyBase + (luckyLvl - 1) * CFG.luckyStep;
            if (Math.random() < chance) { coins *= 2; luckyMsg = ' 🍀 Lucky!'; }
        }

        S.bakeryCoins = (S.bakeryCoins || 0) + coins;
        S.bakeryBakeCount = (S.bakeryBakeCount || 0) + 1;

        if (!S.bakeryInventory) S.bakeryInventory = {};
        const gid = ov.goodie.id;
        const invMax = _getInvMax();
        if ((S.bakeryInventory[gid] || 0) < invMax) {
            S.bakeryInventory[gid] = (S.bakeryInventory[gid] || 0) + 1;
        }

        slot.started = null;

        if (S.bakeryUpgrades.sparkle || luckyMsg) {
            SFX.play('win');
            if (S.bakeryUpgrades.sparkle) _showSparkle(slotIdx);
        }

        toast(`${ov.goodie.icon} ${ov.goodie.name} collected! +${coins} coins${luckyMsg} ♡`);
        if (S.bakeryBakeCount >= CFG.nickThreshold) window.unlockNick('baker');

        if ((S.bakeryUpgrades.autostart || 0) >= 1) {
            _restartPending[slotIdx] = Date.now() + CFG.autoRestartDelayMs;
        }

    } else if (ov.phase === 'burned') {
        slot.started = null;
        delete _restartPending[slotIdx];
        SFX.play('error');
        toast('Burned! 🔥 Oven cleared — try again!');
    }

    scheduleSave();
    _renderOvens();
    _updateBakeryHUD();
}

function _autoCollect(slotIdx) {
    const slot = S.bakeryOvens[slotIdx];
    if (!slot) return;
    const ov = _getOvenState(slot);
    if (ov.phase !== 'ready') return;

    const CFG = window.GAME_BAKERY;
    let coins = _getReward(ov.goodie);
    const luckyLvl = S.bakeryUpgrades.lucky || 0;
    if (luckyLvl > 0) {
        const chance = CFG.luckyBase + (luckyLvl - 1) * CFG.luckyStep;
        if (Math.random() < chance) coins *= 2;
    }

    S.bakeryCoins = (S.bakeryCoins || 0) + coins;
    S.bakeryBakeCount = (S.bakeryBakeCount || 0) + 1;

    if (!S.bakeryInventory) S.bakeryInventory = {};
    const gid = ov.goodie.id;
    const invMax = _getInvMax();
    if ((S.bakeryInventory[gid] || 0) < invMax) {
        S.bakeryInventory[gid] = (S.bakeryInventory[gid] || 0) + 1;
    }

    slot.started = null;
    if (S.bakeryUpgrades.sparkle) _showSparkle(slotIdx);
    if (S.bakeryBakeCount >= CFG.nickThreshold) window.unlockNick('baker');

    if ((S.bakeryUpgrades.autostart || 0) >= 1) {
        _restartPending[slotIdx] = Date.now() + CFG.autoRestartDelayMs;
    }

    scheduleSave();
}

function cancelAutoRestart(slotIdx) {
    delete _restartPending[slotIdx];
    _renderOvens();
}

function cancelBake(slotIdx) {
    const slot = S.bakeryOvens[slotIdx];
    if (!slot || !slot.started) return;
    const ov = _getOvenState(slot);
    if (ov.phase !== 'baking') return;
    slot.started = null;
    SFX.play('error');
    toast('🔄 Bake cancelled — oven is ready for a new recipe!');
    scheduleSave();
    _renderOvens();
    _updateBakeryHUD();
}

function bakerySelectGoodie(slotIdx, goodieId) {
    if (!S.bakeryOvens[slotIdx] || typeof S.bakeryOvens[slotIdx] !== 'object') {
        S.bakeryOvens[slotIdx] = { started: null, goodie: goodieId, sel: goodieId };
    }
    const slot = S.bakeryOvens[slotIdx];
    if (slot.started) return;
    slot.sel = goodieId;
    scheduleSave();
    _renderOvens();
}

function _showSparkle(slotIdx) {
    const ovens = document.querySelectorAll('#bkr-ovens .bkr-oven');
    const el = ovens[slotIdx];
    if (!el) return;
    const sp = document.createElement('div');
    sp.className = 'bkr-sparkle-anim';
    sp.textContent = '✨✨✨';
    el.appendChild(sp);
    setTimeout(() => sp.remove(), 900);
}

// ── Render upgrades ────────────────────────────

function _renderUpgrades() {
    const cont = document.getElementById('bkr-upgrades');
    if (!cont) return;
    const CFG = window.GAME_BAKERY;
    const catLabels = { baking: '⚡ BAKING', shop: '🛒 SHOP', visual: '🎀 VISUAL' };
    let html = '';
    ['baking', 'shop', 'visual'].forEach(cat => {
        html += `<div class="bkr-upg-cat">${catLabels[cat]}</div>`;
        CFG.upgrades.filter(u => u.cat === cat).forEach(upg => {
            const level = S.bakeryUpgrades[upg.id] || 0;
            const max = upg.costs.length;
            const isMax = level >= max;
            const cost = isMax ? 0 : upg.costs[level];
            const canAfford = !isMax && (S.bakeryCoins || 0) >= cost;
            html += `<div class="bkr-upg-row">
                <div class="bkr-upg-icon">${upg.icon}</div>
                <div class="bkr-upg-info">
                    <div class="bkr-upg-name">${upg.name}</div>
                    <div class="bkr-upg-desc">${upg.desc}</div>
                    <div class="bkr-upg-stars">${'★'.repeat(level)}${'☆'.repeat(max - level)}</div>
                </div>
                <div class="bkr-upg-action">
                    ${isMax
                        ? `<span class="bkr-maxed">MAX ✦</span>`
                        : `<button class="bkr-buy-btn${canAfford ? '' : ' dim'}" onclick="buyBakeryUpgrade('${upg.id}')" ${canAfford ? '' : 'disabled'}>${cost}<br><span>coins</span></button>`}
                </div>
            </div>`;
        });
    });
    cont.innerHTML = html;
    _updateBkrHeader();
}

function buyBakeryUpgrade(id) {
    const CFG = window.GAME_BAKERY;
    const upg = CFG.upgrades.find(u => u.id === id);
    if (!upg) return;
    const level = S.bakeryUpgrades[id] || 0;
    if (level >= upg.costs.length) return;
    const cost = upg.costs[level];
    if ((S.bakeryCoins || 0) < cost) { toast('Not enough coins! 🥐'); return; }
    S.bakeryCoins = (S.bakeryCoins || 0) - cost;
    S.bakeryUpgrades[id] = level + 1;
    SFX.play('unlock');
    toast(`${upg.icon} ${upg.name} upgraded to Lv.${level + 1}! ✦`);
    scheduleSave();
    _renderUpgrades();
    _updateBkrHeader();
    const bakePanel = document.getElementById('bkr-panel-bake');
    if (bakePanel && bakePanel.style.display !== 'none') _renderOvens();
}

// ── Render goodies ─────────────────────────────

function _renderGoodies() {
    const cont = document.getElementById('bkr-goodies');
    if (!cont) return;
    const CFG = window.GAME_BAKERY;
    const inv = S.bakeryInventory || {};
    let html = '<div class="bkr-goodies-note">Base values shown — upgrades apply on top.</div><div class="bkr-goodies-grid">';
    CFG.goodies.forEach(g => {
        const owned = S.bakeryUnlocked.includes(g.id);
        const canAffordPts = !owned && (S.bakeryCoins || 0) >= g.unlockCost;
        const itemsOk = !g.unlockItems || g.unlockItems.every(r => (inv[r.id] || 0) >= r.qty);
        const canUnlock = canAffordPts && itemsOk;

        let reqHtml = '';
        if (!owned && g.unlockItems && g.unlockItems.length > 0) {
            reqHtml = `<div class="bkr-req-row">`;
            g.unlockItems.forEach(r => {
                const rg = CFG.goodies.find(x => x.id === r.id);
                const has = (inv[r.id] || 0) >= r.qty;
                reqHtml += `<span class="bkr-req-item${has ? ' ok' : ''}">${r.qty}×<img src="${rg.img}" alt="${rg.icon}" class="bkr-recipe-img-sm"></span>`;
            });
            reqHtml += `</div>`;
        }

        html += `<div class="bkr-goodie-card${owned ? ' owned' : ''}">
            <div class="bkr-goodie-icon"><img src="${g.img}" alt="${g.icon}" class="bkr-recipe-img"></div>
            <div class="bkr-goodie-name">${g.name}</div>
            <div class="bkr-goodie-stat">${CFG.bakeTimeBase + g.bakeBonus}s · +${CFG.yieldBase + g.rewardBonus} coins</div>
            ${reqHtml}
            ${owned
                ? `<div class="bkr-goodie-badge">✦ OWNED</div>`
                : g.unlockCost === 0
                    ? `<div class="bkr-goodie-badge free">FREE</div>`
                    : `<button class="bkr-buy-btn${canUnlock ? '' : ' dim'}" onclick="unlockBakeryGoodie('${g.id}')" ${canUnlock ? '' : 'disabled'}>${g.unlockCost}<br><span>coins</span></button>`}
        </div>`;
    });
    html += '</div>';
    cont.innerHTML = html;
    _updateBkrHeader();
}

function unlockBakeryGoodie(id) {
    const CFG = window.GAME_BAKERY;
    const g = CFG.goodies.find(x => x.id === id);
    if (!g || S.bakeryUnlocked.includes(id)) return;
    if (g.unlockCost > 0 && (S.bakeryCoins || 0) < g.unlockCost) { toast('Not enough coins! 🥐'); return; }

    const inv = S.bakeryInventory || {};
    if (g.unlockItems) {
        for (const req of g.unlockItems) {
            if ((inv[req.id] || 0) < req.qty) {
                const rg = CFG.goodies.find(x => x.id === req.id);
                toast(`Need ${req.qty}× ${rg?.name || req.id} in stock! 📦`);
                return;
            }
        }
        for (const req of g.unlockItems) {
            inv[req.id] = (inv[req.id] || 0) - req.qty;
        }
    }

    if (g.unlockCost > 0) S.bakeryCoins = (S.bakeryCoins || 0) - g.unlockCost;
    S.bakeryUnlocked.push(id);
    SFX.play('unlock');
    toast(`${g.icon} ${g.name} recipe unlocked! ✦`);
    scheduleSave();
    _renderGoodies();
}

// ── Sell tab ───────────────────────────────────

function _tickSellOrders() {
    const CFG = window.GAME_BAKERY;
    const maxCust = _getMaxCustomers();
    for (let i = 0; i < maxCust; i++) {
        const order = _sellOrders[i];
        if (!order) continue;
        if (Date.now() >= order.expiresAt) {
            const loyaltyLvl = S.bakeryUpgrades.loyalty || 0;
            const penaltyMs = Math.max(0, CFG.angerPenaltyMs - loyaltyLvl * CFG.loyaltyPenaltyReduction);
            toast(`${order.face} Customer left angry! Next slot takes longer to fill… 😤`);
            SFX.play('error');
            _sellOrders[i] = null;
            _slotCooldowns[i] = Date.now() + CFG.sellCooldownMs + penaltyMs;
        }
    }
}

function _generateSellOrders() {
    const CFG = window.GAME_BAKERY;
    const maxCust = _getMaxCustomers();
    const timeout = _getSellTimeout();
    const available = CFG.goodies.filter(g => S.bakeryUnlocked.includes(g.id));
    if (available.length === 0) return;

    for (let i = 0; i < maxCust; i++) {
        if (_sellOrders[i]) continue;
        if (Date.now() < _slotCooldowns[i]) continue;

        const goodie = available[Math.floor(Math.random() * available.length)];
        const qty = Math.floor(Math.random() * 3) + 1;
        const mult = _getSellMultiplier();
        const reward = Math.ceil(_getReward(goodie) * qty * mult);

        _sellOrders[i] = {
            goodieId:    goodie.id,
            qty,
            reward,
            face:        _CUST_FACES[Math.floor(Math.random() * _CUST_FACES.length)],
            angryFace:   _ANGRY_FACES[Math.floor(Math.random() * _ANGRY_FACES.length)],
            generatedAt: Date.now(),
            expiresAt:   Date.now() + timeout,
        };
    }
}

function _renderSell() {
    const cont = document.getElementById('bkr-sell');
    if (!cont) return;
    const CFG = window.GAME_BAKERY;
    const inv = S.bakeryInventory || {};
    const invMax = _getInvMax();
    const maxCust = _getMaxCustomers();
    const timeout = _getSellTimeout();
    const totalSec = Math.ceil(timeout / 1000);

    _tickSellOrders();
    _generateSellOrders();

    const unlocked = CFG.goodies.filter(g => S.bakeryUnlocked.includes(g.id));
    let html = `<div class="bkr-sell-label">📦 IN STOCK <span class="bkr-sell-sublabel">(max ${invMax} per recipe)</span></div>
    <div class="bkr-inv-row">`;
    unlocked.forEach(g => {
        const count = inv[g.id] || 0;
        html += `<div class="bkr-inv-item${count === 0 ? ' empty' : ''}">
            <img src="${g.img}" alt="${g.icon}" class="bkr-recipe-img">
            <span class="bkr-inv-count">${count}</span>
        </div>`;
    });
    html += '</div>';

    html += `<div class="bkr-sell-label">👤 CUSTOMERS <span class="bkr-sell-sublabel">(${maxCust} slot${maxCust > 1 ? 's' : ''})</span></div>`;
    html += `<div class="bkr-customer-queue">`;

    for (let i = 0; i < maxCust; i++) {
        const order = _sellOrders[i];
        if (order) {
            const goodie = CFG.goodies.find(g => g.id === order.goodieId);
            const timeLeft = Math.max(0, Math.ceil((order.expiresAt - Date.now()) / 1000));
            const canFulfill = (inv[order.goodieId] || 0) >= order.qty;
            const isQuick = Date.now() < order.generatedAt + timeout * 0.5;
            const tipBonus = isQuick ? Math.ceil(order.reward * (CFG.sellTipMultiplier - 1)) : 0;
            const totalReward = order.reward + tipBonus;
            const isAngry = (timeLeft / totalSec) < CFG.angerFraction;
            const isUrgent = timeLeft <= 10;
            const displayFace = isAngry ? order.angryFace : order.face;
            const tipBadge = tipBonus > 0 ? `<span class="bkr-tip-badge">+${tipBonus} TIP ⚡</span>` : '';
            html += `<div class="bkr-customer-card${isAngry ? ' angry' : ''}">
                <div class="bkr-cust-face">${displayFace}</div>
                <div class="bkr-cust-info">
                    <div class="bkr-cust-wants">${order.qty}× <img src="${goodie.img}" alt="${goodie.icon}" class="bkr-recipe-img-sm"> ${goodie.name}</div>
                    <div class="bkr-cust-reward">+${totalReward} coins ${tipBadge}</div>
                    <div class="bkr-cust-timer${isUrgent ? ' urgent' : ''}">⏱ ${timeLeft}s</div>
                </div>
                <button class="bkr-sell-btn${canFulfill ? '' : ' dim'}" onclick="sellFromInventory(${i})" ${canFulfill ? '' : 'disabled'}>SELL ▶</button>
            </div>`;
        } else if (Date.now() < _slotCooldowns[i]) {
            const cdLeft = Math.ceil((_slotCooldowns[i] - Date.now()) / 1000);
            html += `<div class="bkr-no-customer">Slot ${i + 1}: next customer in ${cdLeft}s… ☕</div>`;
        } else if (unlocked.every(g => (inv[g.id] || 0) === 0)) {
            html += `<div class="bkr-no-customer">Bake something first to attract customers! 🍪</div>`;
        } else {
            html += `<div class="bkr-no-customer">Looking for customers… ☁</div>`;
        }
    }

    html += `</div>`;
    cont.innerHTML = html;
    _updateBkrHeader();
}

function sellFromInventory(orderIdx) {
    const order = _sellOrders[orderIdx];
    if (!order) return;
    const CFG = window.GAME_BAKERY;
    const inv = S.bakeryInventory || {};
    if ((inv[order.goodieId] || 0) < order.qty) { toast('Not enough in stock! 📦'); return; }

    const timeout = _getSellTimeout();
    const isQuick = Date.now() < order.generatedAt + timeout * 0.5;
    const tipBonus = isQuick ? Math.ceil(order.reward * (CFG.sellTipMultiplier - 1)) : 0;
    const totalReward = order.reward + tipBonus;
    const goodie = CFG.goodies.find(g => g.id === order.goodieId);

    inv[order.goodieId] = (inv[order.goodieId] || 0) - order.qty;
    S.bakeryCoins = (S.bakeryCoins || 0) + totalReward;
    SFX.play('win');
    const tipMsg = tipBonus > 0 ? ` (+${tipBonus} quick tip!)` : '';
    toast(`${goodie.icon} Sold ${order.qty}× ${goodie.name}! +${totalReward} coins${tipMsg} ✦`);

    _sellOrders[orderIdx] = null;
    _slotCooldowns[orderIdx] = Date.now() + CFG.sellCooldownMs;
    scheduleSave();
    _renderSell();
    _updateBkrHeader();
}

// ── Bakery HUD ─────────────────────────────────

function _updateBakeryHUD() {
    const hud = document.getElementById('bkr-hud');
    if (!hud) return;
    const CFG = window.GAME_BAKERY;
    if (!CFG) return;

    const count = _activeSlotCount();
    let anyActive = false;
    for (let i = 0; i < count; i++) {
        const phase = _getOvenState(S.bakeryOvens[i]).phase;
        if (phase !== 'idle' || _restartPending[i] !== undefined) { anyActive = true; break; }
    }

    if (!anyActive) { hud.style.display = 'none'; return; }
    hud.style.display = '';

    let html = '';
    for (let i = 0; i < 5; i++) {
        if (i >= count) {
            html += `<div class="bkr-hud-slot-wrap bkr-hud-locked">
                <div class="bkr-hud-porthole"><span class="bkr-hud-slot-icon">🔒</span></div>
                <div class="bkr-hud-slot-label">--</div>
            </div>`;
            continue;
        }
        const ov = _getOvenState(S.bakeryOvens[i]);
        const isPending = _restartPending[i] !== undefined;

        if (isPending) {
            const countdown = Math.max(0, Math.ceil((_restartPending[i] - Date.now()) / 1000));
            html += `<div class="bkr-hud-slot-wrap bkr-hud-idle" onclick="cancelAutoRestart(${i})" title="Click to cancel restart">
                <div class="bkr-hud-porthole"><span class="bkr-hud-slot-icon">🔄</span></div>
                <div class="bkr-hud-slot-label">${countdown}s</div>
            </div>`;
        } else if (ov.phase === 'idle') {
            html += `<div class="bkr-hud-slot-wrap bkr-hud-idle">
                <div class="bkr-hud-porthole"><span class="bkr-hud-slot-icon">·</span></div>
                <div class="bkr-hud-slot-label">idle</div>
            </div>`;
        } else if (ov.phase === 'baking') {
            html += `<div class="bkr-hud-slot-wrap bkr-hud-baking">
                <div class="bkr-hud-porthole"><span class="bkr-hud-slot-icon bkr-wobble"><img src="${ov.goodie.img}" alt="${ov.goodie.icon}"></span></div>
                <div class="bkr-hud-slot-label">${ov.secLeft}s</div>
            </div>`;
        } else if (ov.phase === 'ready') {
            html += `<div class="bkr-hud-slot-wrap bkr-hud-ready" onclick="hudClickOven(${i})">
                <div class="bkr-hud-porthole"><span class="bkr-hud-slot-icon bkr-bounce"><img src="${ov.goodie.img}" alt="${ov.goodie.icon}"></span></div>
                <div class="bkr-hud-slot-label">GET!</div>
            </div>`;
        } else if (ov.phase === 'burned') {
            html += `<div class="bkr-hud-slot-wrap bkr-hud-burned" onclick="hudClickOven(${i})">
                <div class="bkr-hud-porthole"><span class="bkr-hud-slot-icon">🔥</span></div>
                <div class="bkr-hud-slot-label">BURN</div>
            </div>`;
        }
    }

    const cont = document.getElementById('bkr-hud-slots');
    if (cont) cont.innerHTML = html;
}

function hudClickOven(slotIdx) {
    const slot = S.bakeryOvens[slotIdx];
    if (!slot) return;
    const ov = _getOvenState(slot);
    if (ov.phase === 'ready' || ov.phase === 'burned') clickOven(slotIdx);
    _updateBakeryHUD();
}

// ── Exchange ───────────────────────────────────

function exchangeBakeryCoins() {
    const CFG = window.GAME_BAKERY;
    if (!CFG) return;
    const coins = S.bakeryCoins || 0;
    if (coins < CFG.exchangeMinCoins) {
        toast(`Need at least ${CFG.exchangeMinCoins.toLocaleString()} coins to exchange! 🥐`);
        return;
    }
    const blocks = Math.floor(coins / CFG.exchangeMinCoins);
    const ptsGained = blocks * (CFG.exchangeMinCoins / CFG.exchangeCoinsPerPt);
    const coinsSpent = blocks * CFG.exchangeMinCoins;
    S.bakeryCoins -= coinsSpent;
    addPts(ptsGained, 'bakery:exchange');
    SFX.play('win');
    toast(`🥐 Exchanged ${coinsSpent.toLocaleString()} coins → +${ptsGained} pts! ✦`);
    scheduleSave();
    _updateBkrHeader();
    if (_bakeryTab === 'upgrades') _renderUpgrades();
    if (_bakeryTab === 'goodies') _renderGoodies();
}

// Global HUD tick — keeps the widget in sync even when the bakery window is closed
setInterval(_updateBakeryHUD, 1000);

window.openBakery          = openBakery;
window.closeBakery         = closeBakery;
window.setBakeryTab        = setBakeryTab;
window.clickOven           = clickOven;
window.bakerySelectGoodie  = bakerySelectGoodie;
window.buyBakeryUpgrade    = buyBakeryUpgrade;
window.unlockBakeryGoodie  = unlockBakeryGoodie;
window.hudClickOven        = hudClickOven;
window.sellFromInventory   = sellFromInventory;
window.cancelAutoRestart   = cancelAutoRestart;
window.cancelBake          = cancelBake;
window.exchangeBakeryCoins = exchangeBakeryCoins;
